import {buildImportFields} from "./build-import-fields";
import {
    DATASET_ALERT_KEYS,
    DATASET_REQUIREMENT_RULES,
    getCatalogMetaKey,
    getColumnMetaKey,
} from "./dataset-catalog-meta";

/**
 * @param {Object} option - data source option
 * @param {Object} context - buildImportFields context
 * @param {function} t - i18n translate with keyPrefix settingsImport.dataSourceCatalog
 * @param {Object} [extras]
 * @param {boolean} [extras.columnsLoading]
 * @param {boolean} [extras.noCodeGroups]
 */
export const resolveCatalogEntry = (option, context, t, extras = {}) => {
    const metaKey = getCatalogMetaKey(option.id);
    const fields = buildImportFields(option.id, context);
    const datasetPrefix = `datasets.${metaKey}`;

    const columns = fields.map((field) => {
        const columnMetaKey = getColumnMetaKey(field.name);
        const datasetColumnKey = `${datasetPrefix}.columns.${columnMetaKey}`;
        const sharedColumnKey = `sharedColumns.${columnMetaKey}`;

        const description = t(`${datasetColumnKey}.description`, {
            defaultValue: t(`${sharedColumnKey}.description`, {defaultValue: ''}),
        });
        const pointsTo = t(`${datasetColumnKey}.pointsTo`, {
            defaultValue: t(`${sharedColumnKey}.pointsTo`, {defaultValue: ''}),
        });
        const note = t(`${datasetColumnKey}.note`, {
            defaultValue: t(`${sharedColumnKey}.note`, {defaultValue: ''}),
        });

        let sampleValue = '';
        try {
            sampleValue = field.generator?.(1) ?? '';
        } catch {
            sampleValue = '';
        }

        return {
            key: field.name,
            title: field.title,
            requirement: field.optional ? 'optional' : 'required',
            description,
            pointsTo,
            note: note || undefined,
            sampleValue: String(sampleValue ?? ''),
            conditionalGroupIds: [],
        };
    });

    const requirementRules = (DATASET_REQUIREMENT_RULES[metaKey] ?? []).map((rule) => ({
        ...rule,
        message: t(rule.messageKey),
        fieldTitles: rule.fieldNames
            .map((name) => fields.find((f) => f.name === name)?.title)
            .filter(Boolean),
    }));

    const alertKeys = [
        ...(DATASET_ALERT_KEYS[metaKey] ?? []),
        ...(extras.noCodeGroups ? (DATASET_ALERT_KEYS.productCodeAssignmentNoGroups ?? []) : []),
    ];
    const alerts = alertKeys
        .map((entry) => {
            if (typeof entry === 'string') {
                const message = t(entry);
                return message ? {message, severity: 'info'} : null;
            }
            const message = t(entry.key);
            return message ? {message, severity: entry.severity ?? 'info'} : null;
        })
        .filter(Boolean);

    const keywordsRaw = t(`${datasetPrefix}.keywords`, {returnObjects: true, defaultValue: []});
    const keywords = Array.isArray(keywordsRaw) ? keywordsRaw : [];

    const primaryMembership = Array.isArray(option.groups) && option.groups.length
        ? option.groups[0]
        : {group: option.group, groupTitle: option.groupTitle};

    return {
        id: option.id,
        group: primaryMembership?.group ?? option.group,
        groupTitle: primaryMembership?.groupTitle ?? option.groupTitle,
        title: t(`${datasetPrefix}.title`, {defaultValue: option.title}),
        description: t(`${datasetPrefix}.description`, {defaultValue: ''}),
        detail: t(`${datasetPrefix}.detail`, {defaultValue: ''}) || undefined,
        effect: t(`${datasetPrefix}.effect`, {defaultValue: ''}),
        columns,
        requirementRules,
        alerts,
        keywords,
        columnsLoading: Boolean(extras.columnsLoading),
        columnCount: columns.length,
    };
};

export {buildImportFields} from "./build-import-fields";
export {
    buildDataSourceOptions,
    CATALOG_GROUP_ORDER,
    filterDataSourceOptionsByAccess,
    getCatalogGroupSortIndex,
    groupDataSourceOptions,
} from "./data-source-options";
export {
    getCatalogMetaKey,
    RequirementRuleTypes,
} from "./dataset-catalog-meta";
